// src/components/layout/ChangePasswordForm.tsx
import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faKey } from '@/icons';
import '../../components/layout/ChangePasswordForm.css';
import CancelButton from '../common/CancelButton';
import SaveButton from '../common/SaveButton';
import { useApiCall } from '../../hooks/useApiCall';
import { showAlert } from '../../contexts/DialogContext';
import { Toast } from '../../contexts/ToastContext';
import { logger } from '@/utils/logger';
import PasswordRequirements from '../common/PasswordRequirements';
import { evaluatePassword } from '@/utils/passwordRules';

interface ChangePasswordFormProps {
  theme: 'dark' | 'light';
  onClose: () => void;
}

const ChangePasswordForm: React.FC<ChangePasswordFormProps> = ({ onClose }) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const { callApi: callApiSave, isLoading: isSaving } = useApiCall();

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};
    if (!currentPassword) newErrors.currentPassword = 'Digite a senha atual';
    if (!newPassword) newErrors.newPassword = 'Digite a nova senha';
    // Fonte da verdade é o backend #58; o checklist só antecipa (issue #27).
    else if (!evaluatePassword(newPassword).allMet) newErrors.newPassword = 'A senha não atende aos requisitos';
    if (newPassword !== confirmPassword) newErrors.confirmPassword = 'As senhas não coincidem';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    const data = {
      senhaAtual: currentPassword,
      novaSenha: newPassword,
    };

    try {
      await callApiSave('put', '/v1/pessoas/usuarios/minha-senha', data, {
        onSuccess: (data) => {
          if (data.sucesso) {
            Toast.success(data.mensagem);
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
            onClose();
          } else {
            showAlert(data.mensagem, { variant: 'error' });
          }
        },
        onError: (error: unknown) => {
          const apiErr = error as { response?: { data?: { mensagem?: string } } };
          const errorMessage = apiErr.response?.data?.mensagem || 'Erro ao trocar senha';
          showAlert(errorMessage, { variant: 'error' });
        },
      });
    } catch (err) {
      logger.error('Erro ao trocar senha:', err);
    }
  }; // Refatorado para useApiCall, removendo setIsLoading manual; onSuccess verifica sucesso na resposta.

  return (
    <div className="form-container">
      <h2><FontAwesomeIcon icon={faKey} /> Trocar Senha</h2>
      <label className="form-label">
        Senha Atual:
        <input
          type="text"
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          className={`form-input ${errors.currentPassword ? 'input-error' : ''}`}
        />
        {errors.currentPassword && <span className="error-message">{errors.currentPassword}</span>}
      </label>
      <label className="form-label">
        Nova Senha:
        <input
          type="text"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          className={`form-input ${errors.newPassword ? 'input-error' : ''}`}
        />
        {errors.newPassword && <span className="error-message">{errors.newPassword}</span>}
      </label>
      <PasswordRequirements senha={newPassword} />
      <label className="form-label">
        Confirmar Nova Senha:
        <input
          type="text"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          className={`form-input ${errors.confirmPassword ? 'input-error' : ''}`}
        />
        {errors.confirmPassword && <span className="error-message">{errors.confirmPassword}</span>}
      </label>
      <div className="button-container">
        <SaveButton
          isLoading={isSaving}
          onClick={handleSave}
          disabled={!currentPassword || !newPassword || newPassword !== confirmPassword || !evaluatePassword(newPassword).allMet || isSaving}
        />
        <CancelButton onClick={onClose} disabled={isSaving} /> {/* Desabilita durante salvamento. */}
      </div>
    </div>
  );
};

export default ChangePasswordForm;